import { useEffect } from 'react';

interface SeoHeadOptions {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: 'website' | 'article';
  /** Absolute or root-relative path to a 1200x630 social-preview image. Defaults to the site-wide branded card. */
  ogImage?: string;
  canonicalPath?: string;
  jsonLd?: object[];
}

const SITE_URL = 'https://www.millos.uk';
const DEFAULT_OG_IMAGE = '/marketing/og/default.png';

const setMeta = (attr: 'name' | 'property', key: string, val: string) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', val);
};

/** Sets document title/meta/canonical/JSON-LD for a marketing page, mirroring the design handoff's injectSeo pattern. */
export default function useSeoHead(opts: SeoHeadOptions): void {
  const jsonLdKey = opts.jsonLd ? JSON.stringify(opts.jsonLd) : '';
  useEffect(() => {
    document.title = opts.title;
    setMeta('name', 'description', opts.description);
    setMeta('property', 'og:title', opts.ogTitle || opts.title);
    setMeta('property', 'og:description', opts.ogDescription || opts.description);
    setMeta('property', 'og:type', opts.ogType || 'website');

    const imagePath = opts.ogImage || DEFAULT_OG_IMAGE;
    const absoluteImage = imagePath.startsWith('http') ? imagePath : SITE_URL + imagePath;
    setMeta('property', 'og:image', absoluteImage);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', opts.ogTitle || opts.title);
    setMeta('name', 'twitter:description', opts.ogDescription || opts.description);
    setMeta('name', 'twitter:image', absoluteImage);

    if (opts.canonicalPath) {
      let canon = document.head.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement('link');
        canon.setAttribute('rel', 'canonical');
        document.head.appendChild(canon);
      }
      canon.setAttribute('href', SITE_URL + opts.canonicalPath);
    }

    document.head.querySelectorAll('script[data-marketing-ld]').forEach((s) => s.remove());
    (opts.jsonLd || []).forEach((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-marketing-ld', '1');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll('script[data-marketing-ld]').forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.title, opts.description, opts.ogTitle, opts.ogDescription, opts.ogType, opts.ogImage, opts.canonicalPath, jsonLdKey]);
}

export { SITE_URL };
